import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { UserInfo } from '../../../common/model/dto/user-info';
import { UserStatus } from '../../../common/model/dto/user-status.enum';
import { Resource } from '../../../common/model/type/resource.enum';
import { OrderBy } from '../../../common/types';
import useAuthorized from '../../../hooks/use-authorized';
import { useRouteUrl } from '../../../hooks/use-route-url';
import { useUserListColumns } from '../../../hooks/use-user-list-columns';
import OvRecordList from './OvRecordList';

interface OvUserListProps {
  users: UserInfo[];
  orderBy: string;
  orderDir: string;
  onSort: (order: OrderBy) => void;
}

const OvUserList: FC<OvUserListProps> = ({
  users,
  orderBy,
  orderDir,
  onSort,
}) => {
  const { t } = useTranslation();
  const url = useRouteUrl();
  const { pathname, search } = useLocation();
  const applicationOperations = useAuthorized(Resource.Application);
  const userOperations = useAuthorized(Resource.User);
  const readAllowed = userOperations.read || applicationOperations.supervise;
  const getNavigationState = () => ({ prevPath: `${pathname}${search}` });
  const columns = useUserListColumns(getNavigationState);

  return (
    <OvRecordList
      label={t('common.users')}
      records={users}
      columns={columns}
      getKey={(user) => user.id ?? ''}
      getDetailsPath={readAllowed ? (user) => `${url}/${user.id}` : undefined}
      getNavigationState={getNavigationState}
      getRowPresentation={(user) => ({
        isMuted: user.status === UserStatus.Deactivated,
      })}
      orderBy={orderBy}
      orderDir={orderDir}
      onSort={onSort}
    />
  );
};

export default OvUserList;
